import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Chip, Button, Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getMyNotifications, markAsRead, markAllAsRead, getPendingTasks, markTaskAsDone } from '../../api/notificationApi';
import { useUser } from '../../context/userContext';

const PP = "'Poppins', sans-serif";

const TYPE_ICONS = {
    SaleCreated: '🛒', CommissionUpdated: '💰', SaleEdited: '✏️',
    UserCreated: '👤', CommissionCompleted: '✅',
    CommDispersalTask: '⚠️', PricingRemarkTask: '💬',
    SlipPendingApproval: '📋', CommAmendPending: '📝',
    IncentivePendingApproval: '🏆',
    Mention: '📌', NoticeCreated: '📢',
};

export default function NotificationList() {
    const [tab, setTab] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { user } = useUser();
    const canEditAllComm = user?.roleRights?.includes('AllSalesCommList');
    const canEditAllSales = user?.roleRights?.includes('AllSalesList');

    const fetchData = useCallback(async () => {
        try {
            const [notifRes, taskRes] = await Promise.all([
                getMyNotifications(100),
                getPendingTasks()
            ]);
            setNotifications(notifRes.data);
            setTasks(taskRes.data);
        } catch (e) {
            console.error('Error fetching notifications:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleMarkRead = async (id) => { await markAsRead(id); fetchData(); };
    const handleMarkAllRead = async () => { await markAllAsRead(); fetchData(); };
    const handleMarkDone = async (e, id) => {
        e.stopPropagation();
        await markTaskAsDone(id);
        fetchData();
    };

    const getNotifUrl = (n) => {
        // "New agent joined" — display-only, not clickable.
        if (n.type === 'AgentOnboarded') return null;
        // AgentApplication takes precedence — older notifications wrote redirectUrl="/applications" which 404s
        if (n.referenceType === 'AgentApplication') {
            return '/admin/applications';
        }
        if (n.redirectUrl) {
            // Legacy URL rewrites
            if (n.redirectUrl === '/applications') return '/admin/applications';
            if (n.redirectUrl.startsWith('/users/')) return null; // old AgentOnboarded path — also display-only
            return n.redirectUrl;
        }
        const id = n.referenceId;
        if (n.referenceType === 'Sales') {
            return id
                ? (canEditAllSales ? `/accounting/sales/${id}` : `/sales`)
                : '/accounting/sales';
        }
        if (n.referenceType === 'CommissionProgress') {
            return id
                ? (canEditAllComm ? `/accounting/commission/${id}` : `/commissions/detail/${id}`)
                : '/accounting/salesCommissions';
        }
        if (n.referenceType === 'CommissionSlip') {
            return id ? `/accounting/slips?slipId=${id}` : '/accounting/slips';
        }
        return null;
    };

    const handleClick = (e, n) => {
        const url = getNotifUrl(n);
        if (!url) return;
        if (e.button === 1 || e.ctrlKey || e.metaKey) {
            window.open(url, '_blank');
        } else {
            navigate(url);
        }
        if (!n.isRead) markAsRead(n.notificationId);
    };

    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>Loading...</div>;

    const renderItem = (n, isTask = false) => {
        const clickable = !!getNotifUrl(n);
        return (
        <Box key={n.notificationId}
            onMouseUp={(e) => clickable && handleClick(e, n)}
            onMouseDown={(e) => { if (e.button === 1 && clickable) e.preventDefault(); }}
            sx={{
            display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.5,
            borderBottom: '1px solid #F1F5F9', cursor: clickable ? 'pointer' : 'default',
            borderLeft: isTask ? '3px solid #F59E0B' : (n.isRead ? 'none' : '3px solid #1A8CFF'),
            backgroundColor: isTask ? '#FFFBEB' : (n.isRead ? 'transparent' : '#F0F9FF'),
            '&:hover': { backgroundColor: '#F8FAFC' },
        }}>
            <Box sx={{ fontSize: '1.2rem' }}>{TYPE_ICONS[n.type] || '🔔'}</Box>
            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', fontFamily: PP }}>{n.title}</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontFamily: PP }}>{n.message}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: PP }}>{n.timeAgo}</Typography>
                {isTask && (
                    <Button size="small" variant="outlined" onClick={(e) => handleMarkDone(e, n.notificationId)}
                        sx={{ textTransform: 'none', fontFamily: PP, fontSize: '0.65rem', borderRadius: '6px', minWidth: 0, px: 1, py: 0.3, borderColor: '#10B981', color: '#10B981' }}>
                        Done
                    </Button>
                )}
                {!isTask && !n.isRead && <Chip label="New" size="small" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: '#DBEAFE', color: '#1E40AF', fontWeight: 600 }} />}
            </Box>
        </Box>
        );
    };

    return (
        <div>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>Notifications</h2>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: PP }}>Tasks & notifications</p>
                </div>
                {tab === 1 && (
                    <Button variant="outlined" size="small" onClick={handleMarkAllRead}
                        sx={{ textTransform: 'none', fontFamily: PP, borderRadius: '8px', fontSize: '0.8rem' }}>
                        Mark All as Read
                    </Button>
                )}
            </Box>

            <Box sx={{ backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
                    borderBottom: '1px solid #E2E8F0', px: 1,
                    '& .MuiTab-root': { fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' },
                    '& .Mui-selected': { color: '#1A8CFF' },
                    '& .MuiTabs-indicator': { backgroundColor: '#1A8CFF' },
                }}>
                    <Tab label={`Pending Tasks${tasks.length ? ` (${tasks.length})` : ''}`} />
                    <Tab label="All Notifications" />
                </Tabs>

                {tab === 0 && (
                    tasks.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '0.9rem', color: '#94A3B8', fontFamily: PP }}>No pending tasks</Typography>
                        </Box>
                    ) : tasks.map(t => renderItem(t, true))
                )}

                {tab === 1 && (
                    notifications.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '0.9rem', color: '#94A3B8', fontFamily: PP }}>No notifications yet</Typography>
                        </Box>
                    ) : notifications.map(n => renderItem(n, false))
                )}
            </Box>
        </div>
    );
}
